// /var/www/oszczedzam/client/src/pages/Goals.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Dialog, DialogContent, 
  DialogTitle, DialogActions, DialogContentText, CircularProgress, 
  Alert, Snackbar, Paper, Grid, Divider, Chip, Stack
} from '@mui/material';
import { Add as AddIcon, Flag as FlagIcon } from '@mui/icons-material';
import GoalForm from '../components/goals/GoalForm';
import GoalsList from '../components/goals/GoalsList';
import GoalProgress from '../components/goals/GoalProgress';
import SavingsAllocation from '../components/goals/SavingsAllocation';
import { useGoals } from '../hooks/useGoals';
import { generateGoalsSummary, formatAmount, getPriorityColor } from '../utils/goalUtils';

function Goals() {
  const { goals, loading, error, addGoal, updateGoal, deleteGoal, allocateSavings } = useGoals();
  const [openForm, setOpenForm] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [openSavings, setOpenSavings] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const [savingsLoading, setSavingsLoading] = useState(false);

  // Podsumowanie celów
  const goalsSummary = generateGoalsSummary(goals);

  // Dodaj nowy cel oszczędnościowy
  const handleAddGoal = async (goalData) => {
    try {
      await addGoal(goalData);
      setOpenForm(false);
      showNotification('Cel oszczędnościowy został dodany pomyślnie!', 'success');
    } catch (error) {
      console.error('Błąd podczas dodawania celu:', error);
      showNotification('Nie udało się dodać celu. Spróbuj ponownie później.', 'error');
    }
  };

  // Zaktualizuj cel oszczędnościowy
  const handleUpdateGoal = async (goalData) => {
    try {
      await updateGoal(editingGoal.id, goalData);
      setOpenForm(false);
      setEditingGoal(null);
      showNotification('Cel oszczędnościowy został zaktualizowany pomyślnie!', 'success');
    } catch (error) {
      console.error('Błąd podczas aktualizacji celu:', error);
      showNotification('Nie udało się zaktualizować celu. Spróbuj ponownie później.', 'error');
    }
  };

  // Usuń cel oszczędnościowy
  const handleDeleteGoal = async () => {
    try {
      await deleteGoal(goalToDelete);
      setDeleteDialogOpen(false);
      setGoalToDelete(null);
      showNotification('Cel oszczędnościowy został usunięty pomyślnie!', 'success');
    } catch (error) {
      console.error('Błąd podczas usuwania celu:', error);
      showNotification('Nie udało się usunąć celu. Spróbuj ponownie później.', 'error');
    }
  };

  // Dodaj oszczędności do celu
  const handleAllocateSavings = async (allocationData) => {
    try {
      setSavingsLoading(true);
      await allocateSavings(selectedGoal.id, allocationData);
      setOpenSavings(false);
      setSelectedGoal(null);
      showNotification('Oszczędności zostały dodane pomyślnie!', 'success');
    } catch (error) {
      console.error('Błąd podczas dodawania oszczędności:', error);
      showNotification('Nie udało się dodać oszczędności. Spróbuj ponownie później.', 'error');
    } finally {
      setSavingsLoading(false);
    }
  };

  // Otwórz formularz edycji celu
  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setOpenForm(true);
  };

  // Otwórz dialog potwierdzenia usunięcia
  const handleOpenDeleteDialog = (goalId) => {
    setGoalToDelete(goalId);
    setDeleteDialogOpen(true);
  };

  // Otwórz widok szczegółowy celu
  const handleOpenGoalDetail = (goal) => {
    setSelectedGoal(goal);
    setOpenDetail(true);
  };

  // Otwórz dialog dodawania oszczędności
  const handleOpenSavingsAllocation = (goal) => {
    setSelectedGoal(goal);
    setOpenSavings(true);
  };

  // Pokaż powiadomienie
  const showNotification = (message, type) => {
    setNotification({
      open: true,
      message,
      type
    });
  };

  // Zamknij powiadomienie
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Cele oszczędnościowe</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingGoal(null);
            setOpenForm(true);
          }}
        >
          Nowy cel
        </Button>
      </Box>
      
      {/* Podsumowanie celów */}
      {goals.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {goalsSummary.totalGoals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Celów łącznie
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {goalsSummary.completedGoals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Zrealizowanych celów
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4">
                  {formatAmount(goalsSummary.totalSaved)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Suma oszczędności
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4">
                  {goalsSummary.averageProgress}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Średni postęp
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {/* Komunikat o błędzie */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Lista celów */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <GoalsList 
          goals={goals}
          onEdit={handleEditGoal}
          onDelete={handleOpenDeleteDialog}
          onAddSavings={handleOpenSavingsAllocation}
        />
      )}
      
      {/* Formularz celu oszczędnościowego */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <GoalForm 
            onSubmit={editingGoal ? handleUpdateGoal : handleAddGoal}
            initialData={editingGoal}
            isEditing={!!editingGoal}
          />
        </DialogContent>
      </Dialog>
      
      {/* Dialog szczegółów celu */}
      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} maxWidth="md" fullWidth>
        <DialogContent>
          {selectedGoal && <GoalProgress goal={selectedGoal} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetail(false)}>Zamknij</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setOpenDetail(false);
              handleOpenSavingsAllocation(selectedGoal);
            }}
          >
            Dodaj oszczędności
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog dodawania oszczędności */}
      <Dialog open={openSavings} onClose={() => setOpenSavings(false)} maxWidth="md" fullWidth>
        <DialogContent>
          {selectedGoal && (
            <SavingsAllocation 
              goal={selectedGoal}
              onAllocate={handleAllocateSavings}
              loading={savingsLoading}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Dialog potwierdzenia usunięcia */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz usunąć ten cel oszczędnościowy? Tej operacji nie można cofnąć.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anuluj</Button>
          <Button onClick={handleDeleteGoal} color="error" autoFocus>
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Powiadomienia */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.type} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Goals;